import { useCallback, useEffect, useRef, useState } from "react";
import { NipperLogLine, toNipperLogLine } from "utils/nipper/nipperLog";
import { VariantChange } from "utils/nipper/nipperProtocol";
import { NipperReading, NipperSession } from "utils/nipper/nipperSession";

export type { NipperLogKind, NipperLogLine } from "utils/nipper/nipperLog";
export { formatNipperLog, toNipperLogLine } from "utils/nipper/nipperLog";

export type NipperStatus =
  | "unsupported"
  | "idle"
  | "connecting"
  | "connected" // port open, logging in
  | "loggedIn"; // Factory login accepted, reading available

export interface UseNipperSerialOptions {
  /** Delay between Factory login attempts. */
  loginRetryMs?: number;
  /** Wait after reboot before reopening the paired port. */
  rebootReconnectMs?: number;
  /** Max log lines kept. */
  logLimit?: number;
}

const getSerial = (): Serial | undefined =>
  typeof navigator !== "undefined" ? navigator.serial : undefined;

/**
 * Web Serial connection to a Nipper for the EcoSalt Upgrade page:
 * - reconnects to the last paired port on mount and when it is plugged back in;
 * - retries the Factory login until the Nipper accepts it;
 * - reads serial/firmware/variant/runtime once logged in.
 */
export function useNipperSerial(options: UseNipperSerialOptions = {}) {
  const { loginRetryMs = 2000, rebootReconnectMs = 1500, logLimit = 300 } = options;
  const serial = getSerial();
  const [status, setStatus] = useState<NipperStatus>(serial ? "idle" : "unsupported");
  const [reading, setReading] = useState<NipperReading | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<NipperLogLine[]>([]);
  const sessionRef = useRef<NipperSession | null>(null);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);

  const appendLog = useCallback(
    (message: string) => {
      if (!mountedRef.current) return;
      const line = toNipperLogLine(message);
      setLog((prev) => [...prev.slice(-(logLimit - 1)), line]);
    },
    [logLimit],
  );

  const resetState = useCallback(() => {
    sessionRef.current = null;
    if (!mountedRef.current) return;
    setReading(null);
    setStatus(getSerial() ? "idle" : "unsupported");
  }, []);

  /** Asks the Nipper for cell runtime again (`Q`) and updates the reading. */
  const refreshRuntime = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) throw new Error("Nipper is not connected");
    const runtime = await session.readRuntime();
    if (mountedRef.current && sessionRef.current === session) {
      setReading((prev) => (prev ? { ...prev, runtime } : prev));
    }
    return runtime;
  }, []);

  const readAll = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) throw new Error("Nipper is not connected");
    const result = await session.readAll();
    if (mountedRef.current && sessionRef.current === session) setReading(result);
    return result;
  }, []);

  /** Login loop: keeps trying until accepted or the session goes away. */
  const loginLoop = useCallback(
    async (session: NipperSession) => {
      while (mountedRef.current && sessionRef.current === session && session.isOpen) {
        if (await session.login("factory")) {
          appendLog("Factory login accepted");
          if (sessionRef.current !== session) return;
          setStatus("loggedIn");
          try {
            await readAll();
            setError(null);
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          }
          return;
        }
        await new Promise((r) => setTimeout(r, loginRetryMs));
      }
    },
    [appendLog, loginRetryMs, readAll],
  );

  const openPort = useCallback(
    async (port: SerialPort) => {
      if (busyRef.current || sessionRef.current) return;
      busyRef.current = true;
      setStatus("connecting");
      setError(null);
      const session = new NipperSession(port, {
        onLog: appendLog,
        onClosed: () => {
          if (sessionRef.current === session) resetState();
        },
      });
      try {
        await session.open();
        sessionRef.current = session;
        if (!mountedRef.current) {
          await session.close();
          return;
        }
        setStatus("connected");
        appendLog("Serial port open, logging in...");
        void loginLoop(session);
      } catch (e) {
        await session.close();
        resetState();
        setError(`Could not open the serial port: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        busyRef.current = false;
      }
    },
    [appendLog, loginLoop, resetState],
  );

  /** Opens the last paired port without prompting (no user gesture needed). */
  const autoConnect = useCallback(async () => {
    const s = getSerial();
    if (!s || sessionRef.current) return;
    const ports = await s.getPorts();
    if (ports.length) await openPort(ports[0]);
  }, [openPort]);

  /** Connect button: uses the paired port, or asks the user to pick one. */
  const connect = useCallback(async () => {
    const s = getSerial();
    if (!s || sessionRef.current) return;
    const ports = await s.getPorts();
    let port = ports[0];
    if (!port) {
      try {
        port = await s.requestPort();
      } catch {
        return; // user cancelled the picker
      }
    }
    await openPort(port);
  }, [openPort]);

  const disconnect = useCallback(async () => {
    const session = sessionRef.current;
    sessionRef.current = null;
    await session?.close();
    resetState();
  }, [resetState]);

  /** Forgets every paired port so the next Connect shows the port picker. */
  const clearPairedPorts = useCallback(async () => {
    await disconnect();
    const s = getSerial();
    if (!s) return;
    const ports = await s.getPorts();
    for (const port of ports) {
      if (typeof port.forget !== "function") {
        setError("This browser cannot forget paired serial ports.");
        return;
      }
      await port.forget();
    }
    appendLog(`Cleared ${ports.length} paired serial port${ports.length === 1 ? "" : "s"}`);
  }, [appendLog, disconnect]);

  /** Writes variant fields, verifies them, then re-reads the full state. */
  const writeVariant = useCallback(
    async (change: VariantChange) => {
      const session = sessionRef.current;
      if (!session || !reading) throw new Error("Nipper is not connected");
      await session.writeVariant(reading.variant, change);
      return readAll();
    },
    [reading, readAll],
  );

  const writeCellSize = useCallback(
    async (cellSize: number) => writeVariant({ cellSize }),
    [writeVariant],
  );

  /**
   * Sends the Nipper reboot command, closes the port, then auto-reconnects
   * and retries Factory login (same flow as after an unplug/replug).
   */
  const reboot = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) throw new Error("Nipper is not connected");
    appendLog("Sending reboot command...");
    try {
      await session.reboot();
    } catch (e) {
      // The device often drops the link before an ACK; still treat as reboot.
      appendLog(`Reboot send: ${e instanceof Error ? e.message : String(e)}`);
    }
    sessionRef.current = null;
    await session.close();
    resetState();
    appendLog("Waiting for the Nipper to come back...");
    await new Promise((r) => setTimeout(r, rebootReconnectMs));
    if (mountedRef.current) await autoConnect();
  }, [appendLog, autoConnect, rebootReconnectMs, resetState]);

  useEffect(() => {
    mountedRef.current = true;
    const s = getSerial();
    if (!s) return undefined;
    const onConnect = () => void autoConnect();
    const onDisconnect = (event: Event) => {
      const session = sessionRef.current;
      if (session && event.target === session.port) {
        appendLog("Serial device unplugged");
        sessionRef.current = null;
        void session.close();
        resetState();
      }
    };
    s.addEventListener("connect", onConnect);
    s.addEventListener("disconnect", onDisconnect);
    void autoConnect();
    return () => {
      mountedRef.current = false;
      s.removeEventListener("connect", onConnect);
      s.removeEventListener("disconnect", onDisconnect);
      const session = sessionRef.current;
      sessionRef.current = null;
      void session?.close();
    };
  }, [autoConnect, appendLog, resetState]);

  return {
    status,
    reading,
    error,
    log,
    connect,
    disconnect,
    clearPairedPorts,
    readAll,
    refreshRuntime,
    writeCellSize,
    writeVariant,
    reboot,
  };
}
